/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/pages/auth/AuthPage`, `src/pages/home/HomePage`).
 */
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import * as microsoftTeams from '@microsoft/teams-js';
import ChunkLoadErrorBoundary from 'app/components/ChunkLoadErrorBoundary';
import clsx from 'clsx';
import { LayoutContextProvider, frontoffice, isEdelvivesMX, isInPublisher, isLocalhost, isNativeAppWebview } from 'core';
import { getApiHealth } from 'core/crud/appInfo.crud';
import * as auth from 'core/store/auth.duck';
import * as coreEntities from 'core/store/index';
import * as routerHelpers from 'core/utils/RouterHelpers';
import { syncPosthogIdentity } from 'core/utils/posthogIdentity';
import { isTeams } from 'core/utils/teamsTab';
import { logToSentry } from 'core/utils/sentry';
import { isEditorial, isStudent, isTeacher } from 'core/utils/user';
import { defaultConfig } from 'edelvives/config/environment';
import LoginPage from 'edelvives/pages/auth/Login/LoginPage';
import SignUpPage from 'edelvives/pages/auth/SignUp/SignUpPage';
import { ROUTES } from 'edelvives/utils/constants/paths';
import { notificationsTypes } from 'edelvives/utils/notifications';
import { setEdelvivesStyles } from 'edelvives/utils/utils';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { useLastLocation } from 'react-router-last-location';
import { devModeRoutes, externalRoutes, localhostRoutes, privateRoutes } from './routesConfig';

const isDevModeHost = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname.includes('tenant-ai-1');
};

const NotFound = React.lazy(() => import('edelvives/pages/ContentNotFoundPage/ContentNotFoundPage'));
const BarMenuContainer = React.lazy(() => import('edelvives/containers/BarMenuContainer'));
const TokenCallback = React.lazy(() => import('../pages/auth/TokenCallback'));
const AddLicense = React.lazy(() => import('../pages/AddLicensePage/AddLicensePage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage/ResetPasswordPage'));
const RecoverPasswordPage = React.lazy(() => import('../pages/RecoverPasswordPage/RecoverPasswordPage'));

export const APP_IS_PUBLISHER = isInPublisher(defaultConfig) || false;

const loadPosthog = async () => {
  const posthog = await import('posthog-js');
  return posthog.default;
};

let callbackToken = null;

export const RoutesEdelvives = withRouter(({ history }) => {
  const lastLocation = useLastLocation();
  const dispatch = useDispatch();
  const { inProgress } = useMsal();
  const [teamsContentUrl, setTeamsContentUrl] = useState('');
  const [isTeamsDark, setIsTeamsDark] = useState(false);

  checkIfCallbackTokenExists();

  const { isAuthorized, country, roleUser, license, isChangePassword } = useSelector(
    ({ auth }) => ({
      isAuthorized: callbackToken ? auth.user != null && auth.authToken === callbackToken : auth.user != null,
      roleUser: (auth.user && auth.user.role_guid) || null,
      country: auth.user && auth.user.country_guid ? auth.user.country_guid : 'es',
      license: auth.license,
      isChangePassword: Boolean(auth.changePassword),
    }),
    shallowEqual
  );

  const user = useSelector((state) => auth.selectors.getUser(state));
  const oauthAccounts = useSelector((state) => auth.selectors.getOauthAccounts(state));
  const lms = useSelector((state) => coreEntities.organization.selectors.getLms(state));
  const client = useSelector((state) => coreEntities.organization.selectors.getClient(state));
  const tenant = useSelector((state) => coreEntities.organization.selectors.getTenant(state));
  const theme = useSelector((store) => frontoffice.ui.selectors.getTheme(store));
  const userRoleGuid = useSelector((state) => auth.selectors.getUserRoleGuid(state));
  const language = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));

  useEffect(() => {
    // NOTA: La carga de traducciones ahora se gestiona en TranslationsProvider (App.js)
    // Se ha movido la lógica a nivel de app para centralizar la gestión de traducciones por tenant

    if (isEdelvivesMX()) dispatch(coreEntities.i18n.actions.setLanguage('es-MX'));
    if (client) setEdelvivesStyles();
  }, [client]);

  useEffect(() => {
    checkApiHealth();
    if (isAuthorized && client) {
      dispatch(frontoffice.notifications.actions.getNotifications({ offset: 0, pageSize: 4, typeNotifications: notificationsTypes.SOCIAL }));
      dispatch(
        frontoffice.notifications.actions.getNotificationsToDo({
          offset: 0,
          pageSize: 4,
          typeNotifications: notificationsTypes.TODO,
        })
      );
      dispatch(frontoffice.notifications.actions.getNotificationsUnread());
      dispatch(frontoffice.notifications.actions.getNotificationsUnreadToDo());
      dispatch(frontoffice.educationLevels.actions.getEducationLevels({ country, client: client }));
      dispatch(coreEntities.systemSettings.actions.getSystemSettings());
      dispatch(frontoffice.ui.actions.getCustomFonts());
    }
    if (client) {
      dispatch(frontoffice.langs.actions.getLangScopes());
      dispatch(frontoffice.langs.actions.getLangs({ isFilter: true }));
    }

    // dispatch(contentsCourses.actions.getCourses());
  }, [isAuthorized, client]);

  useEffect(() => {
    if (!lms) {
      dispatch(coreEntities.organization.actions.getOrganization());
      dispatch(frontoffice.langs.actions.getLangs({ isFilter: true }));
    }
  }, [lms]);

  useEffect(() => {
    if (user?.guid && tenant?.guid) {
      initPosthog().catch((err) => logToSentry('warning', 'PostHog init failed', { error: err instanceof Error ? err : new Error(String(err)) }));
    }
  }, [user?.guid, tenant?.guid, user?.schools?.[0]?.guid]);

  if (lastLocation && isAuthorized) {
    routerHelpers.saveLastLocation(lastLocation);
  }

  async function initPosthog() {
    const posthog = await loadPosthog();
    let account = oauthAccounts?.filter((acc) => acc.provider !== '');
    let schoolGuid = null;
    let schoolName = null;
    if (user.schools && user.schools.length > 0) {
      schoolGuid = user.schools[0].guid;
      schoolName = user.schools[0].name;
    }

    syncPosthogIdentity(posthog, user.guid, {
      client: tenant.guid,
      email: user.username,
      name: [user.name, user.lastname].filter(Boolean).join(' '),
      role: isStudent(userRoleGuid) ? 'student' : 'teacher',
      is_tangerine_ai: user.is_tangerine_ai === 1,
      source: isTeams() ? 'MS Teams' : user.source,
      schoolGuid,
      schoolName,
      tenantName: tenant?.name,
      username: user?.username,
      provider: account && account.length > 0 ? account[0].provider : 'user and pass',
    });
  }

  async function checkApiHealth() {
    const response = await getApiHealth();
    if (response?.status !== 200) {
      history.push('/error');
    }
  }

  function checkIfCallbackTokenExists() {
    const path = window.location.pathname;
    // We check this in case it comes from the backoffice and we are already logged in with another user
    if (path.includes('/auth/login/')) {
      const splitted = path.split('/');
      callbackToken = splitted.length > 4 ? splitted[splitted.length - 2] : splitted[splitted.length - 1];
    }
    if (path === '/auth/login') {
      callbackToken = null;
    }
  }

  //EXAMPLE OF RESPONSE
  //window.open(`${BASE_URL_}auth/login/${response.data.data.token}/course&${data.guid}&program`);
  //auth/login/${token}/resources`)
  function redirectFromLoginWithToken() {
    const path = window.location.pathname;
    if (path.startsWith('/auth/login/')) {
      const splitted = path.split('/');
      if (splitted.length > 4) {
        const newPath = '/' + splitted[splitted.length - 1].replaceAll('&', '/');
        return newPath;
      }
    }
    return '/home';
  }

  useEffect(() => {
    if (isTeams()) {
      microsoftTeams.app.initialize().then(() => {
        microsoftTeams.app.getContext().then((context) => {
          setIsTeamsDark(context.app.theme === 'dark');
        });
        microsoftTeams.pages.getConfig().then((pageConfig) => {
          if (pageConfig.contentUrl) {
            setTeamsContentUrl(pageConfig.contentUrl.replace(window.location.origin, ''));
          }
        });
      });
    }
  }, []);

  const renderRoutes = (routes) => {
    return routes.map((route, index) => (
      <Route exact key={route.props || index} path={route.path} render={(props) => <route.component {...props} {...route.props} />} />
    ));
  };

  return (
    /* Create `LayoutContext` from current `history` and `menuConfig`. */
    <div
      className={clsx(`theme-${theme}`, 'app-content', `lang-${language}`, {
        'app-content--publisher': isEditorial(userRoleGuid),
        'app-content--student': isStudent(userRoleGuid),
        'app-content--teacher': isTeacher(userRoleGuid),
        'app-content--msteams': isTeams(),
        'app-content--msteams-dark': isTeamsDark,
        'app-content--native-app': isNativeAppWebview(),
      })}
    >
      <ChunkLoadErrorBoundary>
        <LayoutContextProvider history={history} menuConfig={{}}>
          {userRoleGuid && isEditorial(userRoleGuid) && <BarMenuContainer />}
          <Switch>
            {renderRoutes(externalRoutes)}

            {!isAuthorized && (
              /* Redirect to `/auth` when user is not authorized */
              <Switch>
                <Route path={ROUTES.AUTH_LOGIN_TOKEN} component={TokenCallback} />
                <Route path={ROUTES.AUTH_LOGIN} component={LoginPage} />
                <Route path={ROUTES.SIGNUP} component={SignUpPage} />
                <Route path={ROUTES.RECOVER_PASSWORD} component={RecoverPasswordPage} />
                {/* Only redirect if not processing MSAL authentication */}
                {inProgress === InteractionStatus.None && <Redirect to={ROUTES.AUTH_LOGIN} />}
              </Switch>
            )}

            {((isAuthorized && !isChangePassword && license) || roleUser === 'R04') && (
              <Switch>
                <Redirect exact from="/" to={ROUTES.HOME} />
                {isTeams() && <Redirect exact from="/" to={teamsContentUrl} />}
                {isTeams() && <Redirect exact from={ROUTES.HOME} to={teamsContentUrl} />}
                {isLocalhost() && renderRoutes(localhostRoutes)}
                {isDevModeHost() && renderRoutes(devModeRoutes)}
                {renderRoutes(privateRoutes)}
                <Redirect from={ROUTES.AUTH_LOGIN_TOKEN} to={redirectFromLoginWithToken()} />
                <Redirect from={ROUTES.RECOVER_PASSWORD} to={ROUTES.HOME} />
                <Redirect from={ROUTES.AUTH} to={ROUTES.HOME} />
                <Redirect from={ROUTES.TAB} to={ROUTES.HOME} />
                <Route path="*" component={NotFound} />
              </Switch>
            )}

            {isAuthorized && isChangePassword && (
              <Switch>
                <Route path="/reset-password" component={ResetPasswordPage} />
                <Redirect to="/reset-password" />
              </Switch>
            )}

            {isAuthorized && !license && (
              <Switch>
                <Route path={ROUTES.LICENSE} component={AddLicense} />
                <Redirect to={ROUTES.LICENSE} />
              </Switch>
            )}
          </Switch>
        </LayoutContextProvider>
      </ChunkLoadErrorBoundary>
    </div>
  );
});
